import { memo, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ArrowLeft, ClipboardList } from "lucide-react";
import ShellFlipTile from "./ShellFlipTile";

/**
 * Admin secondary horizontal nav — icon tiles + flyout under the active tile.
 * Activity Console flip control sits in the bar chrome (left) so it never overlays page content.
 */
function AdminSecondaryNav({
  modules = [],
  workspaceItems = [],
  expandedKey,
  flippedKey,
  activeTabKey = "",
  pathname = "",
  onModuleClick,
  onChildClick,
  onWorkspaceClick,
  getModuleEnabled,
  showActivityConsole = false,
  activityConsoleOpen = false,
  onActivityConsoleToggle,
}) {
  const barRef = useRef(null);
  const tileRefs = useRef({});
  const [flyoutPos, setFlyoutPos] = useState(null);

  const expandedModule = modules.find((m) => m.name === expandedKey);
  const children = Array.isArray(expandedModule?.children)
    ? expandedModule.children
    : [];

  useEffect(() => {
    if (!expandedKey) return undefined;
    const onDoc = (e) => {
      if (barRef.current && !barRef.current.contains(e.target)) {
        const flyout = e.target.closest?.(".pace-shell-flyout");
        if (!flyout) onModuleClick?.(null);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [expandedKey, onModuleClick]);

  useLayoutEffect(() => {
    if (!expandedKey || !expandedModule || !children.length) {
      setFlyoutPos(null);
      return undefined;
    }
    const el = tileRefs.current[expandedKey];
    if (!el) {
      setFlyoutPos(null);
      return undefined;
    }
    const place = () => {
      const rect = el.getBoundingClientRect();
      const width = 220;
      let left = rect.left;
      if (left + width > window.innerWidth - 8) {
        left = Math.max(8, rect.right - width);
      }
      setFlyoutPos({
        top: rect.bottom + 4,
        left,
        width,
      });
    };
    place();
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [expandedKey, expandedModule, children.length]);

  const isModuleActive = (item) =>
    pathname === item.to || pathname.startsWith(`${item.to}/`);

  const flyout =
    expandedModule && children.length > 0 && flyoutPos
      ? createPortal(
          <div
            className="pace-shell-flyout"
            role="menu"
            aria-label={`${expandedModule.name} sections`}
            style={{
              position: "fixed",
              top: flyoutPos.top,
              left: flyoutPos.left,
              width: flyoutPos.width,
              zIndex: 10040,
            }}
          >
            <p className="pace-shell-flyout-title">{expandedModule.name}</p>
            <div className="pace-shell-flyout-list">
              {children.map((child) => {
                const childActive =
                  isModuleActive(expandedModule) && activeTabKey === child.key;
                return (
                  <button
                    key={child.key}
                    type="button"
                    role="menuitem"
                    className={`pace-shell-flyout-item ${
                      childActive ? "is-active" : ""
                    }`}
                    onClick={() => onChildClick?.(expandedModule, child)}
                  >
                    {child.label}
                  </button>
                );
              })}
            </div>
          </div>,
          document.body
        )
      : null;

  return (
    <nav
      ref={barRef}
      className="pace-shell-secondary"
      aria-label="Admin modules"
    >
      {showActivityConsole ? (
        <div className="pace-shell-activity-slot">
          <button
            type="button"
            className={`pace-shell-activity-flip ${
              activityConsoleOpen ? "is-open" : ""
            }`}
            title={
              activityConsoleOpen ? "Back to previous page" : "Open Activity Console"
            }
            aria-label={
              activityConsoleOpen ? "Back to previous page" : "Open Activity Console"
            }
            aria-pressed={activityConsoleOpen}
            onClick={() => onActivityConsoleToggle?.(activityConsoleOpen)}
          >
            <span className="pace-shell-activity-flip-inner" aria-hidden="true">
              <span className="pace-shell-activity-flip-face pace-shell-activity-flip-front">
                <ClipboardList size={14} strokeWidth={2} />
                <span>Activity Console</span>
              </span>
              <span className="pace-shell-activity-flip-face pace-shell-activity-flip-back">
                <ArrowLeft size={14} strokeWidth={2} />
                <span>Back</span>
              </span>
            </span>
          </button>
        </div>
      ) : null}

      <ul className="pace-shell-secondary-list">
        {modules.map((item) => {
          const enabled = getModuleEnabled ? getModuleEnabled(item) : true;
          const active = isModuleActive(item);
          const flipped = flippedKey === item.name;
          return (
            <li
              key={item.name}
              className="pace-shell-secondary-item"
              ref={(node) => {
                if (node) tileRefs.current[item.name] = node;
                else delete tileRefs.current[item.name];
              }}
            >
              <ShellFlipTile
                variant="line"
                icon={item.icon}
                label={item.tileLabel || item.name}
                title={item.name}
                axis="x"
                active={active || expandedKey === item.name}
                flipped={flipped}
                disabled={!enabled}
                onClick={() => onModuleClick?.(item)}
              />
            </li>
          );
        })}

        {workspaceItems.map((ws) => {
          const active =
            pathname === ws.path ||
            pathname.startsWith(`${ws.path}/`) ||
            pathname === `/admin/workspace/${ws.key}`;
          const Icon = ws.icon;
          return (
            <li key={ws.key} className="pace-shell-secondary-item">
              <ShellFlipTile
                variant="line"
                icon={Icon}
                label={ws.label}
                title={ws.label}
                axis="x"
                active={active}
                flipped={flippedKey === `ws:${ws.key}`}
                onClick={() => onWorkspaceClick?.(ws)}
              />
            </li>
          );
        })}
      </ul>
      {flyout}
    </nav>
  );
}

export default memo(AdminSecondaryNav);
